import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"

interface CountdownTimerProps {
  targetDate: Date
  onComplete?: () => void
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export function CountdownTimer({ 
  targetDate, 
  onComplete, 
  className,
  size = 'md' 
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const [isComplete, setIsComplete] = useState(false)
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +targetDate - +new Date()
      
      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        }
      } else {
        if (!isComplete) {
          setIsComplete(true)
          onComplete?.()
        }
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      }
    }
    
    setTimeLeft(calculateTimeLeft())
    
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)
    
    return () => clearInterval(timer)
  }, [targetDate, onComplete, isComplete])
  
  const sizeConfig = {
    sm: {
      container: 'text-xs gap-2',
      number: 'text-lg',
      label: 'text-xs',
      box: 'w-8 h-8'
    },
    md: {
      container: 'text-sm gap-3',
      number: 'text-2xl',
      label: 'text-sm',
      box: 'w-12 h-12'
    },
    lg: {
      container: 'text-base gap-4',
      number: 'text-4xl',
      label: 'text-base',
      box: 'w-16 h-16'
    }
  }
  
  const config = sizeConfig[size]
  
  if (isComplete) {
    return (
      <div className={cn("text-center", className)}>
        <div className="text-accent font-semibold text-lg animate-pulse-gold">
          Drop is Live! ⚡
        </div>
      </div>
    )
  }
  
  const timeUnits = [
    { value: timeLeft.days, label: 'Days' },
    { value: timeLeft.hours, label: 'Hours' }, 
    { value: timeLeft.minutes, label: 'Min' },
    { value: timeLeft.seconds, label: 'Sec' }
  ].filter(unit => unit.value > 0 || timeLeft.days === 0)
  
  return (
    <div className={cn("flex items-center justify-center", config.container, className)}>
      {timeUnits.map((unit, index) => (
        <div key={unit.label} className="flex items-center">
          <div className="text-center">
            <div className={cn(
              "rounded-lg bg-muted/20 border border-border/50",
              "flex items-center justify-center font-mono font-bold",
              "transition-all duration-300",
              config.box
            )}>
              <span className={cn(config.number, "text-primary")}>
                {unit.value.toString().padStart(2, '0')}
              </span>
            </div>
            <div className={cn(
              "mt-1 text-muted-foreground font-medium",
              config.label
            )}>
              {unit.label}
            </div>
          </div>
          
          {index < timeUnits.length - 1 && (
            <div className={cn(
              "mx-2 text-muted-foreground font-bold",
              config.number
            )}>
              :
            </div>
          )}
        </div>
      ))}
    </div>
  )
}